import React from "react";
import { Container } from "react-bootstrap";

export default function Introduction() {
    return (
        <>
            <div className="">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mt-[32px]">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Introduction</h2>

                    <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                            Welcome to our innovative platform, designed to streamline your social interactions. This platform seamlessly integrates your favorite social media platforms –
                            <span className="font-medium text-[#50B5FF]"> Facebook, Instagram</span> and
                            <span className="font-medium text-[#50B5FF]"> WhatsApp</span> into a single, user-friendly interface. This documentation provides step-by-step instructions for connecting your Facebook, Instagram, and WhatsApp accounts to our social media platform. Each section details the necessary steps to ensure successful integration.
                        </p>

                        <p className="text-gray-700 leading-relaxed mb-8">
                            Thank you for choosing ChatInject, your all-in-one platform designed to simplify business communication. We're thrilled to have you on board and can't wait to show you how our platform can transform the way you connect with your customers.
                        </p>

                        <div className="bg-gradient-to-r from-[#50B5FF]/5 to-blue-50 rounded-lg p-6 mb-8">
                            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                                <span className="mr-2">🚀</span>
                                Why Choose ChatInject?
                            </h3>
                            <p className="text-gray-700 mb-4">
                                With ChatInject, managing your business interactions has never been easier. Here's what you'll love about our platform:
                            </p>
                        </div>

                        <div className="grid gap-6 mb-8">
                            <div className="border-l-4 border-[#50B5FF] pl-6">
                                <h4 className="font-bold text-gray-900 mb-2">All-in-One Integration</h4>
                                <p className="text-gray-700">
                                    Connect and manage WhatsApp, Facebook, Instagram, and Telegram seamlessly from a single dashboard. No more juggling multiple apps—ChatInject brings everything together in one place.
                                </p>
                            </div>

                            <div className="border-l-4 border-[#50B5FF] pl-6">
                                <h4 className="font-bold text-gray-900 mb-2">Meta Integration</h4>
                                <p className="text-gray-700">
                                    Leverage the power of Meta integrations to stay ahead in business. Access real-time chat, fetch customer profiles, and manage your social media engagement effortlessly.
                                </p>
                            </div>

                            <div className="border-l-4 border-[#50B5FF] pl-6">
                                <h4 className="font-bold text-gray-900 mb-2">Live Chat Made Easy</h4>
                                <p className="text-gray-700">
                                    Provide instant customer support with our live chat feature. Whether it's answering queries or closing deals, ChatInject helps you stay responsive and efficient.
                                </p>
                            </div>

                            <div className="border-l-4 border-[#50B5FF] pl-6">
                                <h4 className="font-bold text-gray-900 mb-2">Boost Your Business Efficiency</h4>
                                <p className="text-gray-700">
                                    Save time and resources by automating repetitive tasks and managing all customer interactions from one platform.
                                </p>
                            </div>

                            <div className="border-l-4 border-[#50B5FF] pl-6">
                                <h4 className="font-bold text-gray-900 mb-2">Unified Inbox</h4>
                                <p className="text-gray-700 mb-2">
                                    Receive and manage messages from Facebook, Instagram, and WhatsApp in one convenient place.
                                </p>
                                <p className="text-gray-700">
                                    Easily switch between conversations without the need to open multiple apps.
                                </p>
                            </div>

                            <div className="border-l-4 border-[#50B5FF] pl-6">
                                <h4 className="font-bold text-gray-900 mb-2">Advanced Chat Features</h4>
                                <ul className="text-gray-700 space-y-1">
                                    <li>• Real-time Chat: Enjoy instant messaging across all platforms</li>
                                    <li>• File Sharing: Share documents, photos, and videos effortlessly</li>
                                    <li>• Customizable Notifications: Tailor your notification settings to your preferences</li>
                                    <li>• Dark Mode: Reduce eye strain with the dark mode option</li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-green-50 rounded-lg p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Benefits of Using Our Platform</h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-[#50B5FF] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Increased Efficiency</h4>
                                        <p className="text-gray-700 text-sm">Save time and effort by managing all your social media activities in one place.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-[#50B5FF] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Enhanced User Experience</h4>
                                        <p className="text-gray-700 text-sm">Enjoy a seamless and intuitive interface.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-[#50B5FF] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Improved Productivity</h4>
                                        <p className="text-gray-700 text-sm">Stay organized and focused with our advanced features.</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="w-2 h-2 bg-[#50B5FF] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">Stronger Connections</h4>
                                        <p className="text-gray-700 text-sm">Build deeper connections with friends and family.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Account Creation</h2>

                    <div className="prose prose-lg max-w-none">
                        <p className="text-gray-700 leading-relaxed mb-6">
                            Welcome to ChatInject! To start leveraging the powerful features of our platform, the first step is to create an account. Here's a detailed guide to help you get started with the registration and login process.
                        </p>

                        <div className="space-y-8">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Step 1: Register on ChatInject</h3>
                                <p className="text-gray-700 mb-4">To begin, navigate to the ChatInject website and follow these steps to register:</p>

                                <div className="bg-blue-50 rounded-lg p-6 mb-6">
                                    <h4 className="font-bold text-gray-900 mb-3">1. Choose a Registration Method</h4>
                                    <p className="text-gray-700 mb-4">You can register using any of the following options:</p>

                                    <div className="grid gap-4">
                                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                                            <h5 className="font-semibold text-gray-900 mb-2">Email Address</h5>
                                            <p className="text-gray-700 text-sm">Sign up with your email by entering your details and creating a secure password.</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                                            <h5 className="font-semibold text-gray-900 mb-2">Google Account</h5>
                                            <p className="text-gray-700 text-sm">Simplify the process by linking your Google account for quick registration.</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                                            <h5 className="font-semibold text-gray-900 mb-2">Twitter Account</h5>
                                            <p className="text-gray-700 text-sm">Use your Twitter credentials to register without the need for a separate password.</p>
                                        </div>
                                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                                            <h5 className="font-semibold text-gray-900 mb-2">Microsoft Account</h5>
                                            <p className="text-gray-700 text-sm">Link your Microsoft account to register easily.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-l-4 border-yellow-400 pl-6 mb-6">
                                    <h4 className="font-bold text-gray-900 mb-2">2. Fill in the Required Details</h4>
                                    <p className="text-gray-700 mb-2">If you select email registration, enter your:</p>
                                    <ul className="text-gray-700 space-y-1">
                                        <li>• Full Name</li>
                                        <li>• Email Address</li>
                                        <li>• Password (ensure it meets security standards for added protection)</li>
                                    </ul>
                                </div>

                                <div className="border-l-4 border-green-400 pl-6">
                                    <h4 className="font-bold text-gray-900 mb-2">3. Verify Your Email Address</h4>
                                    <p className="text-gray-700">
                                        If you're signing up via email, a verification link will be sent to your inbox. Click the link to verify your email and activate your account. If you don't see the email in your inbox, check your spam or junk folder.
                                    </p>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Step 2: Login to ChatInject</h3>
                                <p className="text-gray-700 mb-4">Once your account is created, logging in is simple:</p>

                                <div className="bg-gray-50 rounded-lg p-6">
                                    <ol className="text-gray-700 space-y-2">
                                        <li>1. Visit the ChatInject login page</li>
                                        <li>2. Enter your credentials or authorize access through your linked account</li>
                                        <li>3. Multi-Login Options: If you registered with one method (e.g., Google), you can link other methods (e.g., Twitter or Microsoft) later from your account settings for added flexibility</li>
                                    </ol>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Post-Login Setup</h3>
                                <p className="text-gray-700 mb-4">After successfully logging in, you'll be taken to your ChatInject dashboard. Here's what to do next:</p>

                                <div className="grid gap-4">
                                    <div className="bg-gradient-to-r from-[#50B5FF]/10 to-blue-50 rounded-lg p-4">
                                        <h4 className="font-bold text-gray-900 mb-2">Personalize Your Profile</h4>
                                        <p className="text-gray-700 text-sm mb-2">Add a profile picture and update your personal details in the profile section.</p>
                                        <p className="text-gray-700 text-sm">Link any additional social accounts for integration, like WhatsApp, Facebook, Instagram, or Telegram.</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                                        <h4 className="font-bold text-gray-900 mb-2">Explore the Dashboard</h4>
                                        <p className="text-gray-700 text-sm">Familiarize yourself with the layout and options available, including live chat, profile management, and analytics.</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
                                        <h4 className="font-bold text-gray-900 mb-2">Integrate Your Platforms</h4>
                                        <p className="text-gray-700 text-sm">Head to the "Integrations" section to connect your WhatsApp, Facebook, Instagram, or Telegram accounts.</p>
                                    </div>
                                    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-4">
                                        <h4 className="font-bold text-gray-900 mb-2">Start Using ChatInject</h4>
                                        <p className="text-gray-700 text-sm">Dive into the features to streamline your business communication across platforms.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 p-6 bg-[#50B5FF]/5 rounded-lg border border-[#50B5FF]/20">
                            <p className="text-gray-700">
                                Need help? Contact us at{' '}
                                <span className="font-medium text-[#50B5FF]">chatinject@gmail.com</span>
                                {' '}or by filling out the contact form on our{' '}
                                <a href="#" className="text-[#50B5FF] hover:underline">website</a>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
